'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Save, X, Plus } from 'lucide-react'
import type { WorldBuilderResult } from '@creanizer/types'

interface WorldBuilderEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: Partial<WorldBuilderResult>) => void
  worldResult: WorldBuilderResult
  title?: string
}

const WORLD_SCALES = [
  'room', 'building', 'city', 'region', 'country', 
  'continent', 'planet', 'solar_system', 'galaxy', 
  'universe', 'multiverse'
] as const

const SCALE_LABELS: Record<string, string> = {
  room: '部屋',
  building: '建物',
  city: '都市',
  region: '地域',
  country: '国家',
  continent: '大陸',
  planet: '惑星',
  solar_system: '太陽系',
  galaxy: '銀河',
  universe: '宇宙',
  multiverse: '多元宇宙'
}

// 日本語から英語への変換マップ（既存データの互換性のため）
const SCALE_JP_TO_EN: Record<string, string> = {
  '部屋': 'room',
  '建物': 'building',
  '都市': 'city',
  '地域': 'region',
  '国家': 'country',
  '大陸': 'continent',
  '惑星': 'planet',
  '太陽系': 'solar_system',
  '銀河': 'galaxy',
  '宇宙': 'universe',
  '多元宇宙': 'multiverse'
}

// worldScaleを正規化する関数
function normalizeWorldScale(scale: string | undefined): string {
  if (!scale) return 'country'
  
  // 日本語の場合は英語に変換
  if (SCALE_JP_TO_EN[scale]) {
    return SCALE_JP_TO_EN[scale]
  }
  
  // 既存の値をそのまま返す（カスタム値も含めて）
  return scale
}

export function WorldBuilderEditModal({
  isOpen,
  onClose,
  onSave,
  worldResult,
  title = '世界観を編集'
}: WorldBuilderEditModalProps) {
  // 基本情報
  const [editedName, setEditedName] = useState(worldResult.name)
  const [editedConcept, setEditedConcept] = useState(worldResult.concept)
  const [editedSummary, setEditedSummary] = useState(worldResult.summary)
  const [editedGenres, setEditedGenres] = useState(worldResult.genres)
  const [editedWorldScale, setEditedWorldScale] = useState(normalizeWorldScale(worldResult.worldScale))
  
  // パラメータ
  const [editedParameters, setEditedParameters] = useState(worldResult.parameters)
  
  // 時代設定
  const [editedEraName, setEditedEraName] = useState(worldResult.details.era.name)
  const [editedEraDescription, setEditedEraDescription] = useState(worldResult.details.era.description)
  const [editedTechnologyLevel, setEditedTechnologyLevel] = useState(worldResult.details.era.technologyLevel)
  const [editedSocialStructure, setEditedSocialStructure] = useState(worldResult.details.era.socialStructure)
  
  // 独自要素
  const [editedSignature, setEditedSignature] = useState(worldResult.uniqueFeatures?.signature || '')
  const [editedLaws, setEditedLaws] = useState(worldResult.uniqueFeatures?.laws || [])
  const [editedParadoxes, setEditedParadoxes] = useState(worldResult.uniqueFeatures?.paradoxes || [])
  const [editedMysteries, setEditedMysteries] = useState(worldResult.uniqueFeatures?.mysteries || [])
  
  // 新しいジャンル入力
  const [newGenre, setNewGenre] = useState('')
  
  // worldResultが変更されたら編集中の値をリセット
  useEffect(() => {
    setEditedName(worldResult.name)
    setEditedConcept(worldResult.concept)
    setEditedSummary(worldResult.summary)
    setEditedGenres(worldResult.genres)
    setEditedWorldScale(normalizeWorldScale(worldResult.worldScale))
    setEditedParameters(worldResult.parameters)
    setEditedEraName(worldResult.details.era.name)
    setEditedEraDescription(worldResult.details.era.description)
    setEditedTechnologyLevel(worldResult.details.era.technologyLevel)
    setEditedSocialStructure(worldResult.details.era.socialStructure)
    setEditedSignature(worldResult.uniqueFeatures?.signature || '')
    setEditedLaws(worldResult.uniqueFeatures?.laws || [])
    setEditedParadoxes(worldResult.uniqueFeatures?.paradoxes || [])
    setEditedMysteries(worldResult.uniqueFeatures?.mysteries || [])
  }, [worldResult])
  
  const handleAddGenre = () => {
    if (newGenre && !editedGenres.includes(newGenre)) {
      setEditedGenres([...editedGenres, newGenre])
      setNewGenre('')
    }
  }
  
  const handleRemoveGenre = (genre: string) => {
    setEditedGenres(editedGenres.filter(g => g !== genre))
  }
  
  const handleParameterChange = (key: keyof typeof editedParameters, value: number) => {
    setEditedParameters({
      ...editedParameters,
      [key]: value
    })
  }
  
  const handleSave = () => {
    onSave({
      name: editedName,
      concept: editedConcept,
      summary: editedSummary,
      genres: editedGenres,
      worldScale: editedWorldScale,
      parameters: editedParameters,
      details: {
        ...worldResult.details,
        era: {
          name: editedEraName,
          description: editedEraDescription,
          technologyLevel: editedTechnologyLevel,
          socialStructure: editedSocialStructure
        }
      },
      uniqueFeatures: {
        signature: editedSignature,
        laws: editedLaws,
        paradoxes: editedParadoxes,
        mysteries: editedMysteries,
        innovations: worldResult.uniqueFeatures?.innovations || []
      }
    })
    onClose()
  }
  
  const handleCancel = () => {
    // 編集内容をリセット
    setEditedName(worldResult.name)
    setEditedConcept(worldResult.concept)
    setEditedSummary(worldResult.summary)
    setEditedGenres(worldResult.genres)
    setEditedWorldScale(normalizeWorldScale(worldResult.worldScale))
    setEditedParameters(worldResult.parameters)
    setEditedEraName(worldResult.details.era.name)
    setEditedEraDescription(worldResult.details.era.description)
    setEditedTechnologyLevel(worldResult.details.era.technologyLevel)
    setEditedSocialStructure(worldResult.details.era.socialStructure)
    setEditedSignature(worldResult.uniqueFeatures?.signature || '')
    setEditedLaws(worldResult.uniqueFeatures?.laws || [])
    setEditedParadoxes(worldResult.uniqueFeatures?.paradoxes || [])
    setEditedMysteries(worldResult.uniqueFeatures?.mysteries || [])
    onClose()
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* 基本情報 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">基本情報</h3>
            
            <div>
              <Label htmlFor="name">世界名</Label>
              <Input
                id="name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="世界の名前"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="concept">コンセプト</Label>
              <Input
                id="concept"
                value={editedConcept}
                onChange={(e) => setEditedConcept(e.target.value)}
                placeholder="世界のコンセプト"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="summary">概要</Label>
              <Textarea
                id="summary"
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
                placeholder="世界の概要"
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>世界規模</Label>
              <select
                value={editedWorldScale}
                onChange={(e) => setEditedWorldScale(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                {/* 現在の値が標準の選択肢にない場合は、カスタム値として最初に表示 */}
                {!WORLD_SCALES.includes(editedWorldScale as any) && (
                  <option value={editedWorldScale}>
                    {editedWorldScale} (カスタム値)
                  </option>
                )}
                {WORLD_SCALES.map(scale => (
                  <option key={scale} value={scale}>
                    {SCALE_LABELS[scale]}
                  </option>
                ))}
              </select>
              {/* カスタム値の場合の注意書き */}
              {!WORLD_SCALES.includes(editedWorldScale as any) && (
                <p className="text-xs text-muted-foreground mt-1">
                  現在の値はカスタム値です。標準の選択肢から選ぶと変更されます。
                </p>
              )}
            </div>
            
            <div>
              <Label>ジャンル</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {editedGenres.map((genre, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => handleRemoveGenre(genre)}
                  >
                    {genre} ×
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newGenre}
                  onChange={(e) => setNewGenre(e.target.value)}
                  placeholder="新しいジャンルを追加"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddGenre()
                    }
                  }}
                />
                <Button onClick={handleAddGenre} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* パラメータ */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">世界パラメータ</h3>
            
            {Object.entries(editedParameters).map(([key, value]) => (
              <div key={key}>
                <div className="flex justify-between items-center mb-2">
                  <Label>{getParameterLabel(key)}</Label>
                  <span className="text-sm font-medium">{value}</span>
                </div>
                <Slider
                  value={[value]}
                  onValueChange={([newValue]) => handleParameterChange(key as keyof typeof editedParameters, newValue)}
                  min={1}
                  max={10}
                  step={1}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
          
          {/* 時代設定 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">時代設定</h3>
            
            <div>
              <Label htmlFor="eraName">時代名</Label>
              <Input
                id="eraName"
                value={editedEraName}
                onChange={(e) => setEditedEraName(e.target.value)}
                placeholder="時代の名前"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="eraDescription">時代の説明</Label>
              <Textarea
                id="eraDescription"
                value={editedEraDescription}
                onChange={(e) => setEditedEraDescription(e.target.value)}
                placeholder="時代の詳細な説明"
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="technologyLevel">技術レベル</Label>
              <Input
                id="technologyLevel"
                value={editedTechnologyLevel}
                onChange={(e) => setEditedTechnologyLevel(e.target.value)}
                placeholder="技術レベルの説明"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="socialStructure">社会構造</Label>
              <Textarea
                id="socialStructure"
                value={editedSocialStructure}
                onChange={(e) => setEditedSocialStructure(e.target.value)}
                placeholder="社会構造の説明"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          
          {/* 独自要素 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">独自要素</h3>
            
            <div>
              <Label htmlFor="signature">シグネチャー（世界の特徴）</Label>
              <Textarea
                id="signature"
                value={editedSignature}
                onChange={(e) => setEditedSignature(e.target.value)}
                placeholder="この世界を象徴する特徴"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function getParameterLabel(key: string): string {
  const labels: Record<string, string> = {
    magicLevel: '魔法レベル',
    scienceLevel: '科学レベル',
    civilizationLevel: '文明レベル',
    dangerLevel: '危険度',
    mysteryLevel: '神秘性',
    complexityLevel: '複雑性',
    uniquenessLevel: '独自性',
    chaosLevel: '混沌度'
  }
  return labels[key] || key
}